import { parse } from 'minidown/parser'
import { type MinidownNode, MinidownNodeType } from 'minidown/nodes'
import { type ReactNode, useMemo, createElement, Fragment } from 'react'

interface Props {
  /**
   * The Minidown content to render. Can be either the raw source or the
   * parsed AST.
   */
  value: string | MinidownNode
  /**
   * See {@link MinidownClassNames}.
   */
  classNames?: MinidownClassNames
}

/**
 * Class names to apply to rendered tags.
 */
export interface MinidownClassNames {
  p?: string
  a?: string
  strong?: string
  em?: string
}

/**
 * Renders Minidown as React components.
 */
export function MinidownContent({ value, classNames }: Props) {
  const visit = useMemo(() => createVisitor(classNames), [classNames])

  const root = useMemo<MinidownNode>(() => {
    if (typeof value !== 'string') {
      return value
    }

    try {
      return parse(value)
    } catch (err) {
      console.error(
        `Unable to parse Minidown. The Minidown source is:\n\n${value}`,
        err,
      )
      // Return the Minidown source as-is if we can't render it.
      return {
        type: MinidownNodeType.Root,
        children: [{ type: MinidownNodeType.Text, value }],
      }
    }
  }, [value])
  return createElement(Fragment, {}, ...visit(root))
}

/**
 * Creates a visitor that renders a React node for each AST node.
 * @param classNames
 */
function createVisitor(classNames: MinidownClassNames | undefined) {
  return function visit(node: MinidownNode): ReactNode[] {
    switch (node.type) {
      case MinidownNodeType.Root:
        return node.children.flatMap(visit)
      case MinidownNodeType.Text:
        return [node.value]
      case MinidownNodeType.Break:
        return [createElement('br')]
      case MinidownNodeType.Emphasis:
        return [
          createElement(
            'em',
            {
              className: classNames?.em,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Strong:
        return [
          createElement(
            'strong',
            {
              className: classNames?.strong,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Paragraph:
        return [
          createElement(
            'p',
            {
              className: classNames?.p,
            },
            ...node.children.flatMap(visit),
          ),
        ]
      case MinidownNodeType.Link:
        return [
          createElement(
            'a',
            {
              className: classNames?.a,
              href: node.url,
              target: '_blank',
            },
            ...node.children.flatMap(visit),
          ),
        ]
      default:
        return []
    }
  }
}
